import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import * as R from 'ramda';

import TeamContext, {
  baseUserGroup,
  initialPayPeriodYear,
  Section,
  View,
} from '@atom/components/teamPortal/TeamContext';
import { GET_PAY_PERIODS } from '@atom/graph/payPeriod';
import { GET_USER } from '@atom/graph/user';
import {
  GET_USER_GROUP_TREE,
  GET_USERS_AND_USER_GROUPS,
} from '@atom/graph/userGroup';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import {
  INIT_PAGINATION,
  useTeamNavigation,
} from '@atom/hooks/useTeamNavigation';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Progress } from '@atom/mui';
import {
  getUserGroupTableData,
  getUserGroupTableTotalCount,
} from '@atom/selectors/userGroupSelectors';
import { PayPeriod, PayPeriods, PayPeriodsInput } from '@atom/types/payPeriod';
import { UserDetail, UserInput } from '@atom/types/user';
import {
  UserFilters,
  UserGroupChildrenConnectionInput,
  UserGroupTreeType,
} from '@atom/types/userGroups';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { FILES_USERS_CSV_DOWNLOAD_ENDPOINT } from '@atom/utilities/endpoints';
import { buildEndpointWithParamsFromData } from '@atom/utilities/requestUtilities';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import {
  convertDateToMillisGMTMidday,
  getPayPeriodWeeks,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import RolesTable from './rolesTable/RolesTable';
import TeamMapView from './teamMapView/TeamMapView';
import TeamSidebar from './teamSidebar/TeamSidebar';
import TimeSheet from './timeSheet/TimeSheet';
import TimeSheetList from './timeSheetList/TimeSheetList';
import UserGroupsTable from './userGroupsTable/UserGroupsTable';
import UsersTable from './usersTable/UsersTable';
import TeamHeader from './TeamHeader';

import classes from './teamPortal.module.css';

const TEAM_PARAM_TYPES = {
  view: SearchParamType.STRING,
  teamId: SearchParamType.STRING,
  payPeriodYear: SearchParamType.NUMBER,
  payPeriod: SearchParamType.NUMBER,
  timeSheetUserId: SearchParamType.STRING,
  page: SearchParamType.NUMBER,
  limit: SearchParamType.NUMBER,
  activeGroupId: SearchParamType.STRING,
  activePathIds: SearchParamType.ARRAY,
};

interface TeamParams {
  view: string;
  teamId: string;
  payPeriodYear: number;
  payPeriod: number;
  timeSheetUserId: string;
  page: number;
  limit: number;
  activeGroupId: string;
  activePathIds: string[];
}

const TeamPortal = () => {
  const userProfile = useUserProfile();
  const preferences = usePreferences();
  const [searchParamsObject, { updateSearchParams }] =
    useNavigation(TEAM_PARAM_TYPES);

  // @ts-ignore
  const {
    view,
    teamId,
    payPeriodYear,
    payPeriod,
    timeSheetUserId,
    page,
    limit,
    activeGroupId,
    activePathIds,
  }: TeamParams = searchParamsObject;

  const { teamNavigation, setTeamNavigation } = useTeamNavigation();
  const { expanded } = teamNavigation;

  const [activeGroup, setActiveGroup] =
    useState<UserGroupTreeType>(baseUserGroup);
  const [activePath, setActivePath] = useState<UserGroupTreeType[]>([
    baseUserGroup,
  ]);

  const [activeTimeSheetGroup, setActiveTimeSheetGroup] = useState();
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<PayPeriod>();

  const [isMapView, setIsMapView] = useState<boolean>(false);
  const [csvDownloadEndpoint, setCsvDownloadEndpoint] = useState<string>('');
  const [filters, setFilters] = useState<UserFilters>({});
  const [open, setOpen] = useState<Set<string>>(new Set(['']));

  useEffect(() => {
    // Remove teamId from params when navigating to non-timesheet view
    if (view !== View.TIME_SHEET && view !== View.TIME_SHEET_LIST) {
      updateSearchParams({ teamId: null });
    }
    if (!view) {
      const initValues = {
        view: View.DIRECTORY,
        page: page || INIT_PAGINATION.page,
        limit: limit || INIT_PAGINATION.limit,
        activeGroupId: activeGroupId || baseUserGroup.id,
        activePathIds: (activePathIds || [baseUserGroup.id]).join(),
        ...(preferences?.timeTracking?.timesheet
          ? { payPeriodYear: payPeriodYear || initialPayPeriodYear }
          : {}),
      };

      if (!preferences?.timeTracking?.timesheet) {
        updateSearchParams({ payPeriodYear: null });
      }

      updateSearchParams(initValues);
    }
  }, [view]);

  const [getPayPeriods, { loading: payPeriodsLoading, data: payPeriodsData }] =
    useLazyQuery<{ payPeriods: PayPeriods }, { input: PayPeriodsInput }>(
      GET_PAY_PERIODS,
      {
        onCompleted: data => {
          if (!payPeriod) {
            updateSearchParams({
              payPeriod: data?.payPeriods?.currentPeriod || 1,
            });
          }
        },
      },
    );

  const payPeriods = useMemo(
    () =>
      payPeriodsData?.payPeriods || {
        periods: [],
        currentPeriod: 0,
        availableYears: [],
      },
    [payPeriodsData],
  );

  useEffect(() => {
    if (preferences?.timeTracking?.timesheet && payPeriodYear) {
      getPayPeriods({
        variables: {
          input: {
            year: payPeriodYear,
            currentDate: convertDateToMillisGMTMidday(new Date()),
          },
        },
      });
    }
  }, [payPeriodYear]);

  useEffect(() => {
    // Set selected pay period and payPeriodWeeks when payPeriod param changes
    if (preferences?.timeTracking?.timesheet && payPeriod) {
      const currentPayPeriod = payPeriods.periods.find(
        item => item.period === Number(payPeriod),
      );

      const newPayPeriodWeeks = getPayPeriodWeeks(currentPayPeriod);

      setSelectedPayPeriod(currentPayPeriod);
      setTeamNavigation({
        payPeriodWeeks: newPayPeriodWeeks,
      });
    }
  }, [payPeriod, payPeriods]);

  const [getUser, { loading: userLoading, data: userData }] = useLazyQuery<
    { user: UserDetail },
    { id: string; input: UserInput }
  >(GET_USER, {
    fetchPolicy: 'network-only',
  });

  const user: Partial<UserDetail> = R.pathOr({}, ['user'], userData);

  useEffect(() => {
    getUser({
      variables: {
        id: userProfile.userId,
        input: {
          includeTimeEntryApproverInfo: true,
          includeRestoredBudgets: true,
        },
      },
    });
  }, []);

  const {
    loading: treeLoading,
    data: userGroupTreeData,
    refetch: refetchTree,
  } = useQuery(GET_USER_GROUP_TREE, {
    variables: {
      input: {
        showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
      },
    },
    fetchPolicy: 'network-only',
  });

  const userGroupTree = useMemo(
    () => R.pathOr(baseUserGroup, ['userGroupsTree'], userGroupTreeData),
    [userGroupTreeData],
  );

  const currentUserHasUserGroups = useMemo(
    () => R.pathOr([], ['userGroups'], user).length > 0,
    [user],
  );

  const {
    loading: tableLoading,
    data: tableData,
    refetch: refetchTable,
  } = useQuery<
    { userGroup: UserGroupTreeType },
    { id: string; input: UserGroupChildrenConnectionInput }
  >(GET_USERS_AND_USER_GROUPS, {
    variables: {
      id: activeGroup.id,
      input: {
        page,
        limit,
      },
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const endpoint = buildEndpointWithParamsFromData(
      FILES_USERS_CSV_DOWNLOAD_ENDPOINT,
      filters,
    );

    setCsvDownloadEndpoint(endpoint);
  }, [view, filters]);

  useEffect(() => {
    setOpen(new Set([...open, ...activePath.map(path => path.id)]));
  }, [activePath.length]);

  const toggleOpen = (id: string) => {
    setOpen(toggleFromSet(open, id));
  };

  const toggleExpanded = (section: Section) => {
    setTeamNavigation({ expanded: toggleFromSet(expanded, section) });
  };

  const handleGroup = (
    userGroup: UserGroupTreeType,
    path: UserGroupTreeType[],
    skipInitPagination?: boolean,
    replaceUrl: boolean = true,
  ) => {
    updateSearchParams(
      {
        view: View.DIRECTORY,
        activePathIds: path?.map(({ id }) => id).join(),
        activeGroupId: userGroup?.id,
        ...(!skipInitPagination ? { page: INIT_PAGINATION.page } : {}),
        ...(!skipInitPagination ? { limit: INIT_PAGINATION.limit } : {}),
      },
      replaceUrl,
    );
  };

  useEffect(() => {
    // Select active directory group when activeGroupId is set or userGroupTree refreshes
    if (!treeLoading && activeGroupId) {
      // Building newActiveGroup by reducing the activePathIds
      //   in searchParams to the group at the end of the path,
      //   building newActivePath as it goes for breadcrumbs
      const newActivePath = [userGroupTree];
      const newActiveGroup =
        activeGroupId !== baseUserGroup.id
          ? R.takeLast(activePathIds.length - 1, activePathIds).reduce(
              (parentGroup, groupId) => {
                const childGroup = !parentGroup?.userGroups?.length
                  ? parentGroup
                  : parentGroup.userGroups.find(({ id }) => id === groupId);
                newActivePath.push(childGroup);
                return childGroup;
              },
              userGroupTree,
            )
          : baseUserGroup;

      setActiveGroup(newActiveGroup);
      setActivePath(newActivePath);
    }
  }, [activeGroupId, userGroupTree]);

  const timeEntryApproverInfo = useMemo(
    () => R.pathOr([], ['timeEntryApproverInfo'], user),
    [user],
  );

  useEffect(() => {
    // Auto-select first timeSheet group if user hits timeSheet page without teamId
    if (
      preferences?.timeTracking?.timesheet &&
      (view === View.TIME_SHEET_LIST ||
        (timeSheetUserId && view === View.TIME_SHEET)) &&
      isNilOrEmpty(teamId) &&
      !isNilOrEmpty(timeEntryApproverInfo)
    ) {
      updateSearchParams({ teamId: timeEntryApproverInfo[0]?.id });
    }
  }, [searchParamsObject, timeEntryApproverInfo]);

  useEffect(() => {
    // Select active group when teamID is set
    if (teamId && timeEntryApproverInfo) {
      const selectedGroup = timeEntryApproverInfo.find(
        ({ id }) => id === teamId,
      );

      setActiveTimeSheetGroup(selectedGroup);
    }
  }, [teamId, timeEntryApproverInfo]);

  const getContent = () => {
    const views = {
      [View.DIRECTORY]: <UserGroupsTable />,
      [View.ACTIVE]: <UsersTable />,
      [View.PENDING]: <UsersTable />,
      [View.DEACTIVATED]: <UsersTable />,
      [View.TIME_SHEET]: <TimeSheet />,
      [View.TIME_SHEET_LIST]: <TimeSheetList />,
      [View.ROLES]: <RolesTable />,
    };

    return userLoading ? <Progress /> : views[view];
  };

  const contextValue = {
    treeLoading,
    userGroupTree,
    refetchTree,
    setFilters,
    isMapView,
    setIsMapView,
    handleGroup,
    csvDownloadEndpoint,
    open,
    toggleOpen,
    totalCount: getUserGroupTableTotalCount(tableData),
    table: getUserGroupTableData(tableData),
    tableLoading,
    refetchTable,
    payPeriodsLoading,
    payPeriods,
    userLoading,
    timeEntryApproverInfo,
    currentUserHasUserGroups,
    toggleExpanded,
    activeTimeSheetGroup,
    setActiveTimeSheetGroup,
    selectedPayPeriod,
    activeGroup,
    activePath,
  };

  return (
    <TeamContext.Provider value={contextValue}>
      <div className={classes.portalContainer}>
        <TeamHeader />
        {isMapView ? (
          <div className={classes.mapContainer}>
            <TeamMapView />
          </div>
        ) : (
          <div className={classes.contentContainer}>
            <TeamSidebar />
            <div className={classes.rightPane}>{getContent()}</div>
          </div>
        )}
      </div>
    </TeamContext.Provider>
  );
};

export default TeamPortal;
